import { useEffect, useRef } from 'react';

// Jump-navigation only — not a router. Category sections all live on the
// same page (spec §7/§25: no complicated navigation), this just smooth-
// scrolls to an anchor and tracks which section is active.
export function CategoryNav({ categories, visibleCategories = categories, activeCategoryId, onSelect, variant, searchQuery, onSearchChange, resultCount }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !activeCategoryId) return;

    const activeLink = Array.from(container.querySelectorAll('[data-category-id]'))
      .find((link) => link.dataset.categoryId === String(activeCategoryId));
    if (!activeLink) return;

    const containerRect = container.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    const edgePadding = 10;
    let nextScrollLeft = container.scrollLeft;

    if (linkRect.left < containerRect.left + edgePadding) {
      nextScrollLeft -= containerRect.left + edgePadding - linkRect.left;
    } else if (linkRect.right > containerRect.right - edgePadding) {
      nextScrollLeft += linkRect.right - containerRect.right + edgePadding;
    }

    if (nextScrollLeft !== container.scrollLeft) {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ left: nextScrollLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      } else {
        container.scrollLeft = nextScrollLeft;
      }
    }
  }, [activeCategoryId, visibleCategories]);

  if (categories.length === 0) return null;

  return (
    <>
      <div className="rm-nav__search" role="search">
        <span className="rm-nav__search-icon" aria-hidden="true" />
        <input
          className="rm-nav__search-input"
          type="search"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search dishes"
          aria-label="Search dishes"
        />
        {searchQuery && (
          <button type="button" className="rm-nav__search-clear" onClick={() => onSearchChange('')} aria-label="Clear dish search">
            ×
          </button>
        )}
      </div>
      <nav className={`rm-nav rm-nav--${variant}`} aria-label="Menu categories">
        <div className="rm-nav__content">
          <div className="rm-nav__meta" aria-live="polite">
            {searchQuery ? `${resultCount} result${resultCount === 1 ? '' : 's'}` : 'Browse menu'}
          </div>
          <div className="rm-nav__scroll" ref={scrollRef}>
            {visibleCategories.map((category) => (
              <a
                key={category.id}
                data-category-id={category.id}
                href={`#category-${category.id}`}
                className="rm-nav__link"
                aria-current={category.id === activeCategoryId ? 'true' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  onSelect(category.id);
                }}
              >
                <CategoryIcon name={category.name} />
                <span>{category.name}</span>
              </a>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}

function CategoryIcon({ name }) {
  const category = name.toLowerCase();

  if (/drink|beverage|cocktail|coffee|tea/.test(category)) {
    return (
      <svg className="rm-nav__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 3h10l-1 18H8L7 3Z" />
        <path d="M8 8h8M9 12h6M5 3h14" />
      </svg>
    );
  }

  if (/dessert|sweet|cake|bakery|pastr/.test(category)) {
    return (
      <svg className="rm-nav__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 20h16M6 20V12h12v8M4 12l8-8 8 8H4Z" />
        <path d="M9 16v1M15 16v1" />
      </svg>
    );
  }

  if (/classic|side|starter/.test(category)) {
    return (
      <svg className="rm-nav__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 11h16M6 11a6 6 0 0 1 12 0M4 14h16M6 17h12" />
        <path d="M12 3v2" />
      </svg>
    );
  }

  return (
    <svg className="rm-nav__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21c-4.5-2.4-7-6.1-7-10.2C5 7.4 7.2 5 10 5c1.1 0 1.8.5 2 1.4C12.2 5.5 13 5 14 5c2.8 0 5 2.4 5 5.8 0 4.1-2.5 7.8-7 10.2Z" />
      <path d="M12 20c0-5 1.7-8.3 4-10" />
    </svg>
  );
}
